import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Store } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useUpdateStore } from '@/features/stores';
import { useStoreEditable } from '@/shared/store/use-store-editable';

interface AlertsForm {
  lowStockInApp: boolean;
  lowStockEmail: boolean;
}

/**
 * `settings.inventory`: the bell and the email sent when a level falls to its reorder point. Both
 * are off until the store switches them on (D-175); the webhook event fires either way.
 */
export function LowStockAlertsCard({ store }: { store: Store }) {
  const { t } = useTranslation(['inventory', 'common']);
  const message = useErrorMessage();
  const { editable } = useStoreEditable({ store, permission: 'settings.write' });
  const update = useUpdateStore(store.id, 'inventory:toasts.alertsSaved');
  const form = useForm<AlertsForm>({
    defaultValues: {
      lowStockInApp: store.settings.inventory.lowStockInApp,
      lowStockEmail: store.settings.inventory.lowStockEmail,
    },
  });
  const noAddress = !store.supportEmail && !store.contactEmail;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({ settings: { inventory: values } });
      form.reset(values);
    } catch (error) {
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  const toggle = (name: keyof AlertsForm, label: string, hint: string) => (
    <div className="flex items-center justify-between gap-4 rounded-md border p-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor={`alerts-${name}`}>{label}</Label>
        <p className="text-muted-foreground text-xs">{hint}</p>
      </div>
      <Switch
        id={`alerts-${name}`}
        checked={form.watch(name)}
        disabled={!editable}
        onCheckedChange={(checked) => form.setValue(name, checked, { shouldDirty: true })}
      />
    </div>
  );

  return (
    <Card className="gap-0 py-0" data-tour="settings.locations.alerts">
      <CardHeader className="px-6 py-4">
        <CardTitle className="text-sm">{t('inventory:alerts.title')}</CardTitle>
        <CardDescription>{t('inventory:alerts.description')}</CardDescription>
      </CardHeader>
      <CardContent className="px-6 pb-5">
        <form onSubmit={submit} noValidate className="flex flex-col gap-3">
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          {toggle('lowStockInApp', t('inventory:alerts.inApp'), t('inventory:alerts.inAppHint'))}
          {toggle('lowStockEmail', t('inventory:alerts.email'), t('inventory:alerts.emailHint'))}
          {form.watch('lowStockEmail') && noAddress ? (
            <p className="text-muted-foreground text-xs" role="status">
              {t('inventory:alerts.noAddress')}
            </p>
          ) : null}
          {editable ? (
            <div>
              <Button
                type="submit"
                size="sm"
                disabled={update.isPending || !form.formState.isDirty}
              >
                {update.isPending ? t('common:actions.saving') : t('inventory:alerts.submit')}
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}
